import React, { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../redux/store";
import { getWhoIsWorkingDetails } from "../../../redux/features/whoIsIn";
import { useSelector } from "react-redux";
import { NgwAvatar } from "../../../components/common";

const CheckInCard: React.FC = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const raw_id = location.state?.raw_data_id;
    const { isLoadingWhoIsInDetails, whoIsInDetails } = useSelector((state: RootState) => state.whoIsIn);

    // Fetch the data using raw_id
    useLayoutEffect(() => {
        dispatch(
            getWhoIsWorkingDetails({
                raw_id,
            })
        );
    }, [raw_id]);

    if (isLoadingWhoIsInDetails) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 p-4 space-y-4">
            <div className="flex flex-col items-center space-y-2">
                {/* Profile picture */}
                <div className="w-20 h-20 rounded-full bg-gray-300 flex items-center justify-center">
                    <NgwAvatar src={whoIsInDetails?.employee?.image_url} name={whoIsInDetails?.employee?.emp_name} />
                </div>
                {/* Name */}
                <h2 className="text-lg font-semibold">{whoIsInDetails?.employee?.emp_name || "Unknown Employee"}</h2>
                {/* Location Map */}
                <div className="w-full h-20 bg-gray-100 rounded-md flex items-center justify-center">
                    {whoIsInDetails?.geo_details_json?.lat && whoIsInDetails?.geo_details_json?.long ? (
                        <div className="w-full h-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5 14.5 7.6 14.5 9 13.4 11.5 12 11.5z" />
                            </svg>
                            <span className="text-xs ml-2">
                                {whoIsInDetails.geo_details_json.lat}, {whoIsInDetails.geo_details_json.long}
                            </span>
                        </div>
                    ) : (
                        <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5 14.5 7.6 14.5 9 13.4 11.5 12 11.5z" />
                        </svg>
                    )}
                </div>
                {/* Punch In Time */}
                <p className="text-gray-600 font-medium">
                    {whoIsInDetails?.status?.status_txt || ""}{" "}
                    {whoIsInDetails?.punch_datetime
                        ? `at ${new Date(whoIsInDetails.punch_datetime).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                          })}`
                        : ""}
                </p>
            </div>

            {/* Info Sections */}
            <div className="space-y-2">
                <InfoRow icon="📍" label="Site" value={whoIsInDetails?.site?.site_name || "N/A"} />
                <InfoRow icon="📋" label="Code" value={whoIsInDetails?.work_code?.work_code_name || "N/A"} />
            </div>

            <div className="space-y-2">
                <InfoRow icon="🔒" label="Department" value={whoIsInDetails?.department?.department_name || "N/A"} />
                <InfoRow icon="📝" label="Notes" value={whoIsInDetails?.notes || "No notes available"} />
            </div>

            {/* Tags */}
            {whoIsInDetails?.tags && whoIsInDetails.tags.length > 0 && (
                <div className="border border-gray-200 rounded-md p-3">
                    <div className="flex items-center mb-2">
                        <span className="mr-2">🏷️</span>
                        <span className="font-medium">Tags</span>
                    </div>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                        {whoIsInDetails.tags.map((tag, index) => (
                            <li key={index}>{tag.project || "Unnamed tag"}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

interface InfoRowProps {
    icon: string;
    label: string;
    value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => {
    return (
        <div className="flex items-center justify-between border border-gray-100 rounded-md p-2">
            <div className="flex items-center space-x-2 text-gray-600">
                <span>{icon}</span>
                <span>{label}</span>
            </div>
            <div className="text-gray-800 font-medium">{value}</div>
        </div>
    );
};

export default CheckInCard;
